import './ProgressDialog.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectActionById, updateAction } from '../../../redux/slices/actionsSlice';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Progress } from 'antd';
import { RTU } from '../../../types/RTU';
import { ActionArtifact } from '../../../types/actionTypes';
import { RootState } from '../../../redux/store/store';
import {
  formatActionPhaseLabel,
  formatActionProgressSummary,
  summarizeActionProgress,
} from '../../../utils/actionProgress';
import { useNavigate } from 'react-router-dom';
import actionRunsService, { CancelActionRunRequest } from '../../../services/actionRunsService';
import { actionRunDetailToAction } from '../../../utils/actionRunMapper';
import { getCancelPresentation } from '../../../utils/actionPresentation';
import {
  getActionStatusLabel,
  isTerminalActionStatus,
  resolveActionDisplayStatus,
} from '../../../utils/actionStatusConfig';
import ActionModalShell from '../../../features/actions/ActionModalShell';
import ActionStatusIcon from '../../common/ActionStatusIcon';
import ActionTargetRow from '../../common/ActionTargetRow';
import { sanitizeApiError } from '../../../utils/apiErrorSanitizer';

interface ProgressDialogProps {
  rtus?: RTU[];
  actionName: string | '';
  actionTimestamp: string;
  visible: boolean;
  onHide: () => void;
  actionId: number | null;
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
  rtus = [],
  actionName,
  actionTimestamp,
  visible,
  onHide,
  actionId,
}) => {
  const { t } = useTranslation();
  const [isCanceling, setIsCanceling] = useState(false);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<number | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const latestAction = useSelector((state: RootState) =>
    actionId ? selectActionById(state, actionId) ?? null : null
  );
  const summary = useMemo(
    () => (latestAction ? summarizeActionProgress(latestAction) : null),
    [latestAction]
  );
  const displayStatus = resolveActionDisplayStatus(latestAction);
  const cancelPresentation = getCancelPresentation(latestAction?.commandHints);

  const refreshActionSnapshot = useCallback(async () => {
    if (!actionId) return;
    setLoadingSnapshot(true);
    try {
      const detail = await actionRunsService.fetchActionRun(actionId);
      dispatch(updateAction(actionRunDetailToAction(detail)));
    } catch (error: any) {
      const sanitized = sanitizeApiError(error, { endpointCategory: 'action-progress-refresh', t });
      setCommandError(
        sanitized.status === 403
          ? t('actionsModal.progress.accessChanged')
          : sanitized.message
      );
    } finally {
      setLoadingSnapshot(false);
    }
    // `t` deliberately excluded - it is only used for error-message strings,
    // and including it here re-fired this fetch on every unrelated
    // Redux/i18next re-render (see src/locales/i18n.ts). This is the exact
    // modal from the original polling-storm report, missed in the first
    // pass because the bug was fixed in the Dashboard/ActionsWorkbench
    // components but not here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, dispatch]);

  useEffect(() => {
    if (!visible || !actionId) return;
    setCommandError(null);
    void refreshActionSnapshot();
  }, [actionId, refreshActionSnapshot, visible]);

  useEffect(() => {
    if (isTerminalActionStatus(displayStatus)) {
      setIsCanceling(false);
    }
  }, [displayStatus]);

  const handleRTUClick = (rtuId: number) => {
    // The activity center and other global surfaces open this dialog without
    // a page-scoped rtus list; navigation must work from rtuId alone. The
    // matching RTU (when available) is only passed as router state so the
    // destination page can skip a redundant fetch, never as a precondition.
    const matchingRTU = rtus.find((rtu) => rtu.id === rtuId);
    const actionRunParam = actionId ? `&actionRunId=${actionId}` : '';
    navigate(`/rtupage/${rtuId}?view=actions${actionRunParam}`, matchingRTU ? { state: { rtu: matchingRTU } } : undefined);
  };

  const handleDownloadArtifact = async (artifact: ActionArtifact) => {
    if (!actionId) return;
    setDownloadingArtifactId(artifact.id);
    try {
      await actionRunsService.downloadArtifact(actionId, artifact);
    } catch (error: any) {
      setCommandError(sanitizeApiError(error, { endpointCategory: 'artifact-download', t }).message);
    } finally {
      setDownloadingArtifactId(null);
    }
  };

  const handleCancelAction = async () => {
    if (!actionId || !cancelPresentation.enabled || cancelPresentation.scope === 'NONE') return;
    const request: CancelActionRunRequest = {
      cancelScope: cancelPresentation.scope,
      targetRunIds: latestAction?.commandHints?.cancellableTargetRunIds ?? [],
      scopeRevision: latestAction?.commandHints?.scopeRevision ?? '',
    };
    setIsCanceling(true);
    setCommandError(null);
    try {
      await actionRunsService.cancelActionRun(actionId, request);
      await refreshActionSnapshot();
    } catch (error: any) {
      const sanitized = sanitizeApiError(error, { endpointCategory: 'action-cancel', t });
      if (sanitized.status === 409 && sanitized.code === 'ACTION_CANCEL_SCOPE_CHANGED') {
        await refreshActionSnapshot();
        setCommandError(t('actionsModal.progress.scopeChanged'));
      } else if (sanitized.status === 409) {
        await refreshActionSnapshot();
        setCommandError(sanitized.message || t('actionsModal.progress.cancelTooLate'));
      } else if (sanitized.status === 403) {
        await refreshActionSnapshot();
        setCommandError(t('actionsModal.progress.accessChangedNoCancel'));
      } else {
        setCommandError(sanitized.message || t('actionsModal.progress.cancelFailed'));
      }
    } finally {
      setIsCanceling(false);
    }
  };

  const footerContent = () => {
    if (isCanceling) {
      return (
        <div className="center-message">
          <span aria-hidden="true">...</span>
          <span>{t('actionsModal.progress.cancelling')}</span>
        </div>
      );
    }

    if (isTerminalActionStatus(displayStatus)) {
      return (
        <div className="center-message">
          <span>{getActionStatusLabel(displayStatus, t)}</span>
        </div>
      );
    }

    return (
      <Button
        danger
        type="primary"
        onClick={() => void handleCancelAction()}
        loading={isCanceling}
        disabled={loadingSnapshot || !cancelPresentation.enabled}
      >
        {cancelPresentation.label}
      </Button>
    );
  };

  return (
    <ActionModalShell
      open={visible}
      onCancel={() => {
        setIsCanceling(false);
        onHide();
      }}
      title={t('actionsModal.progress.title', { action: actionName })}
      width={560}
      footer={footerContent()}
    >
      <div className="dialog-content">
        {latestAction && summary ? (
          <div className="progress-dialog__summary-card">
            <div className="progress-dialog__summary-header">
              <ActionStatusIcon status={displayStatus} />
              <span className="progress-dialog__summary-status">{getActionStatusLabel(displayStatus, t)}</span>
              <span className="progress-dialog__summary-text">{formatActionProgressSummary(summary, t)}</span>
              <span className="progress-dialog__phase-text">{formatActionPhaseLabel(latestAction.actionPhase, t)}</span>
            </div>
            <Progress percent={Math.round(summary.progressRatio * 100)} showInfo={false} status={isTerminalActionStatus(displayStatus) ? 'normal' : 'active'} />
            {actionId ? (
              <Button
                type="link"
                size="small"
                className="progress-dialog__full-action-link"
                onClick={() => navigate(`/actions?actionRunId=${actionId}`)}
              >
                {t('actionsModal.progress.openInAllActions', { defaultValue: 'Open in All Actions' })}
              </Button>
            ) : null}
          </div>
        ) : null}
        {commandError ? <div className="progress-dialog__command-error" role="alert">{commandError}</div> : null}

        <div className="scrollable-content">
          {latestAction?.actionDetails.map((detail, index) => {
            const rtuId = detail.rtuId ?? null;
            const matchingRtu = rtus.find((rtu) => rtu.id === rtuId);
            const serialNo = detail.rtuSerialNo || matchingRtu?.serialNo || null;
            const targetName = detail.rtuName || matchingRtu?.name || null;
            const targetLabel = targetName
              ? rtuId ? `${targetName} (${rtuId})` : targetName
              : rtuId
                ? t('actionsModal.progress.rtuTarget', { rtuId })
                : t('actionsModal.progress.unknownTarget');
            const artifact = latestAction?.artifacts?.find((item) => item.targetRunId === detail.id);
            return (
              <ActionTargetRow
                key={String(detail.id ?? `${serialNo ?? 'unknown'}:${rtuId ?? index}`)}
                status={detail.status}
                name={targetLabel}
                nameTooltip={serialNo ? t('actionsModal.progress.serialTooltip', {
                  defaultValue: 'Serial: {{serialNo}}',
                  serialNo,
                }) : undefined}
                ip={detail.ip}
                phase={detail.phaseLabel}
                durationMs={detail.durationMs}
                responseMessage={detail.responseMessage}
                transfer={detail.transfer}
                timeline={detail.timeline}
                onActivate={rtuId ? () => handleRTUClick(rtuId) : undefined}
                onDownloadArtifact={artifact ? () => void handleDownloadArtifact(artifact) : undefined}
                downloadLabel={artifact ? t('actionsModal.artifacts.download', { defaultValue: 'Download {{fileName}}', fileName: artifact.fileName }) : undefined}
                downloadingArtifact={artifact ? downloadingArtifactId === artifact.id : false}
                artifactFileName={artifact?.fileName}
                compact
              />
            );
          })}
        </div>
      </div>
    </ActionModalShell>
  );
};

export default ProgressDialog;
